"use client";

import {
  Avatar,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { getAvatarColor } from "utils/taskUtils";

/**
 * Textarea with lightweight @mention autocomplete. Tracks `mentionedUserIds` alongside the
 * free-text value so callers can forward both to the backend (for notifications, highlighting,
 * etc.) without having to re-parse the text themselves.
 */
const MentionTextarea = ({
  value,
  onChange,
  mentionedUserIds = [],
  onMentionedUserIdsChange,
  users = [],
  placeholder = "Write a message... Type @ to mention someone",
  rows = 3,
  maxRows = 14,
  className = "",
  disabled = false,
}) => {
  const textareaRef = useRef(null);
  const [query, setQuery] = useState(null); // null = closed, string = current @-query
  const [triggerIndex, setTriggerIndex] = useState(null);

  const suggestions = useMemo(() => {
    if (query == null) return [];
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u) => u?.name && (!q || u.name.toLowerCase().includes(q)))
      .slice(0, 6);
  }, [query, users]);

  const detectMention = (text, cursor) => {
    const uptoCursor = text.slice(0, cursor ?? text.length);
    const match = uptoCursor.match(/(?:^|\s)@([A-Za-z][\w'.-]*)?$/);
    if (!match) {
      setQuery(null);
      setTriggerIndex(null);
      return;
    }
    setTriggerIndex(uptoCursor.lastIndexOf("@"));
    setQuery(match[1] || "");
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange?.(text);
    detectMention(text, e.target.selectionStart);
  };

  const handleCaretMove = (e) => {
    detectMention(e.target.value, e.target.selectionStart);
  };

  const insertMention = (user) => {
    if (triggerIndex == null) return;
    const text = value || "";
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, triggerIndex);
    const after = text.slice(cursor);
    const insertion = `@${user.name} `;
    onChange?.(`${before}${insertion}${after}`);

    const nextIds = Array.from(new Set([...(mentionedUserIds || []), user.id]));
    onMentionedUserIdsChange?.(nextIds);

    setQuery(null);
    setTriggerIndex(null);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = before.length + insertion.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  return (
    <div className={`relative ${className}`}>
      {/* minRows rather than rows, so a long comment grows the box as it is typed instead of
          scrolling inside a fixed two lines. maxRows keeps it from pushing the send button off. */}
      <TextField
        fullWidth
        multiline
        minRows={rows}
        maxRows={maxRows}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleChange}
        onKeyUp={handleCaretMove}
        onClick={handleCaretMove}
        disabled={disabled}
        inputRef={textareaRef}
      />
      {query != null && suggestions.length > 0 && (
        <ClickAwayListener onClickAway={() => setQuery(null)}>
          <Paper
            elevation={3}
            className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto"
          >
            <List dense>
              {suggestions.map((user) => (
                <ListItemButton key={user.id} onClick={() => insertMention(user)}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: 11,
                      mr: 1,
                      bgcolor: getAvatarColor(user.id),
                    }}
                  >
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText primary={user.name} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default MentionTextarea;
